import React, { useState } from "react";
import {
  IconTool,
  IconPlus,
  IconSearch,
  IconCheck,
  IconClock,
  IconCurrencyDollar,
  IconAlertCircle,
  IconUser,
  IconBuildingStore,
  IconTools,
  IconShieldCheck
} from "@tabler/icons-react";
import {
  useMaintenanceWorkOrders,
  useAssets,
  recordMaintenance,
  updateMaintenance
} from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetMaintenanceTab() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);

  const { data: workOrders, error, isLoading, mutate } = useMaintenanceWorkOrders();
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    work_order_type: "Corrective / Breakdown Repair",
    priority: "Medium",
    issue_description: "",
    service_provider_type: "Internal Technician",
    vendor_name: "",
    assigned_technician: "In-house Maintenance Tech",
    estimated_cost: "",
    start_date: new Date().toISOString().split("T")[0]
  });

  const [completeFormData, setCompleteFormData] = useState({
    labor_cost: "0",
    parts_cost: "0",
    other_cost: "0",
    total_cost: "0",
    resolution_notes: "Repaired and fully operational.",
    quality_cleared_by: "Chief Engineer"
  });

  const filteredOrders = Array.isArray(workOrders)
    ? workOrders.filter((wo) => {
        if (statusFilter !== "all" && wo.status !== statusFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          wo.work_order_number?.toLowerCase().includes(q) ||
          wo.asset_code?.toLowerCase().includes(q) ||
          wo.asset_name?.toLowerCase().includes(q) ||
          wo.issue_description?.toLowerCase().includes(q) ||
          wo.assigned_technician?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenCreateModal = () => {
    setFormData({
      asset_id: assets?.[0]?.id || "",
      work_order_type: "Corrective / Breakdown Repair",
      priority: "Medium",
      issue_description: "",
      service_provider_type: "Internal Technician",
      vendor_name: "",
      assigned_technician: "In-house Maintenance Tech",
      estimated_cost: "",
      start_date: new Date().toISOString().split("T")[0]
    });
    setIsModalOpen(true);
  };

  const handleCreateWorkOrder = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset");
      return;
    }
    if (!formData.issue_description) {
      toast.error("Please describe the issue");
      return;
    }

    try {
      await recordMaintenance(formData);
      toast.success("Maintenance Work Order created successfully");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to create work order");
    }
  };

  const handleOpenCompleteModal = (wo) => {
    setSelectedWO(wo);
    setCompleteFormData({
      labor_cost: "0",
      parts_cost: "0",
      other_cost: "0",
      total_cost: wo.estimated_cost ? String(wo.estimated_cost) : "0",
      resolution_notes: "Repairs completed and functional tests passed.",
      quality_cleared_by: "Maintenance Supervisor"
    });
    setIsCompleteModalOpen(true);
  };

  const handleFinishWorkOrder = async (e) => {
    e.preventDefault();
    if (!selectedWO) return;

    const total =
      Number(completeFormData.labor_cost || 0) +
      Number(completeFormData.parts_cost || 0) +
      Number(completeFormData.other_cost || 0);

    try {
      await updateMaintenance(selectedWO.id, {
        status: "Completed",
        end_date: new Date().toISOString().split("T")[0],
        labor_cost: Number(completeFormData.labor_cost || 0),
        parts_cost: Number(completeFormData.parts_cost || 0),
        other_cost: Number(completeFormData.other_cost || 0),
        actual_cost: total,
        resolution_notes: completeFormData.resolution_notes,
        quality_cleared_by: completeFormData.quality_cleared_by
      });
      toast.success("Work Order completed and quality cleared!");
      setIsCompleteModalOpen(false);
      setSelectedWO(null);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to complete work order");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconTool className="w-6 h-6 text-restro-green" />
            Maintenance & Repair Work Orders
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage corrective repairs, external contractor jobs, spare parts costs, and quality clearances.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search work orders, techs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-60 text-gray-800 dark:text-gray-200"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
          >
            <option value="all">All Statuses</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>

          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Create Work Order
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Work Order</th>
                <th className="px-6 py-4">Asset</th>
                <th className="px-6 py-4">Issue Description</th>
                <th className="px-6 py-4">Assigned Tech / Vendor</th>
                <th className="px-6 py-4">Cost (NPR)</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading maintenance work orders...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconTools className="w-12 h-12 mx-auto mb-2 opacity-30 text-restro-green" />
                    No work orders found. Click "Create Work Order" to schedule a maintenance job.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((wo) => (
                  <tr key={wo.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900 dark:text-white font-mono text-xs">
                        {wo.work_order_number || `WO-${wo.id}`}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{wo.work_order_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {wo.asset_name || "Asset #" + wo.asset_id}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        {wo.asset_code || `AST-${wo.asset_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-medium text-gray-800 dark:text-gray-200 max-w-xs truncate">
                        {wo.issue_description}
                      </div>
                      <div className="text-xs text-gray-400">
                        Priority: <span className="font-medium text-amber-600">{wo.priority || "Normal"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {wo.assigned_technician || wo.vendor_name || "Unassigned"}
                      </div>
                      <div className="text-xs text-gray-400">{wo.service_provider_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        Rs. {Number(wo.actual_cost || wo.cost || wo.estimated_cost || 0).toLocaleString()}
                      </div>
                      {wo.actual_cost > 0 && <div className="text-xs text-emerald-500">Actual Billed</div>}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          wo.status === "Completed"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                            : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                        }`}
                      >
                        {wo.status === "Completed" ? <IconCheck className="w-3 h-3" /> : <IconClock className="w-3 h-3" />}
                        {wo.status || "Open"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {wo.status !== "Completed" ? (
                        <button
                          onClick={() => handleOpenCompleteModal(wo)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-lg transition"
                        >
                          <IconShieldCheck className="w-3.5 h-3.5" />
                          Complete & Clear
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 font-medium">Cleared</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Create Work Order */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconTool className="w-5 h-5 text-restro-green" />
                Dispatch Maintenance Work Order
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateWorkOrder} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Asset *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name} ({a.location || a.room_number || "Main Building"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Work Order Type *
                  </label>
                  <select
                    value={formData.work_order_type}
                    onChange={(e) => setFormData({ ...formData, work_order_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="Corrective / Breakdown Repair">Corrective / Breakdown Repair</option>
                    <option value="Emergency Breakdown">Emergency Breakdown</option>
                    <option value="Preventive Maintenance">Preventive Maintenance</option>
                    <option value="Refurbishment / Overhaul">Refurbishment / Overhaul</option>
                    <option value="Inspection / Calibration">Inspection / Calibration</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-medium"
                  >
                    <option value="Low">Low - Within 7 days</option>
                    <option value="Medium">Medium - Within 48 hours</option>
                    <option value="High">High - Same day</option>
                    <option value="Critical">Critical - Immediate emergency</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Issue Description & Scope of Work *
                </label>
                <textarea
                  rows="2"
                  value={formData.issue_description}
                  onChange={(e) => setFormData({ ...formData, issue_description: e.target.value })}
                  placeholder="e.g. Compressor not cooling; refrigerant leak suspected"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Service Provider *
                  </label>
                  <select
                    value={formData.service_provider_type}
                    onChange={(e) => setFormData({ ...formData, service_provider_type: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="Internal Technician">Internal Technician / Staff</option>
                    <option value="External Contractor / Vendor">External Contractor / Vendor</option>
                    <option value="Manufacturer Warranty Service">Manufacturer Warranty Service</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {formData.service_provider_type.includes("External") ? "Vendor Name" : "Assigned Staff / Lead"}
                  </label>
                  <input
                    type="text"
                    value={formData.assigned_technician}
                    onChange={(e) => setFormData({ ...formData, assigned_technician: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Scheduled Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Estimated Cost (NPR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Create Work Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Complete Work Order */}
      {isCompleteModalOpen && selectedWO && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <IconShieldCheck className="w-5 h-5 text-restro-green" />
                  Sign-off & Complete Work Order
                </h3>
                <div className="text-xs text-gray-400">WO #{selectedWO.work_order_number || selectedWO.id}</div>
              </div>
              <button
                onClick={() => setIsCompleteModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleFinishWorkOrder} className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3 bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Labor Cost (Rs)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={completeFormData.labor_cost}
                    onChange={(e) => setCompleteFormData({ ...completeFormData, labor_cost: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Parts Cost (Rs)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={completeFormData.parts_cost}
                    onChange={(e) => setCompleteFormData({ ...completeFormData, parts_cost: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Other / Misc (Rs)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={completeFormData.other_cost}
                    onChange={(e) => setCompleteFormData({ ...completeFormData, other_cost: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Resolution & Work Performed
                </label>
                <textarea
                  rows="3"
                  value={completeFormData.resolution_notes}
                  onChange={(e) => setCompleteFormData({ ...completeFormData, resolution_notes: e.target.value })}
                  placeholder="Detail parts replaced, tests conducted, and operational verification..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Quality Clearance Sign-off By *
                </label>
                <input
                  type="text"
                  value={completeFormData.quality_cleared_by}
                  onChange={(e) => setCompleteFormData({ ...completeFormData, quality_cleared_by: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsCompleteModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Verify & Complete Work Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
